import { useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'

import { useAuth } from '../lib/auth'
import { Logo, ThemeToggle, cx } from './ui'

const NAV = [
  { to: '/generator', label: 'Генератор' },
  { to: '/pricing', label: 'Тарифи' },
  { to: '/docs', label: 'Документація' },
  { to: '/standard', label: 'Стандарт НБУ' },
]

export function PublicLayout() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur dark:border-slate-800 dark:bg-slate-950/85">
        <div className="container-page flex h-16 items-center justify-between gap-4">
          <Link to="/" aria-label="QR24 — на головну">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    'rounded-lg px-3 py-2 text-sm font-medium transition',
                    isActive
                      ? 'text-brand-700 dark:text-brand-300'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Link to="/app" className="btn-primary">
                Кабінет
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn-ghost hidden sm:inline-flex">
                  Увійти
                </Link>
                <Link to="/register" className="btn-primary">
                  Почати безкоштовно
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="Меню"
              className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 md:hidden"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={open ? 'M6 6l12 12M18 6 6 18' : 'M4 7h16M4 12h16M4 17h16'} strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {open && (
          <nav className="border-t border-slate-200 px-4 py-2 md:hidden dark:border-slate-800">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  )
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
      <div className="container-page grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-3 max-w-xs text-sm text-slate-500 dark:text-slate-400">
            Генератор і API для платіжних QR-кодів стандарту НБУ. Версії v1, v2 та v3.
          </p>
        </div>

        <FooterColumn
          title="Продукт"
          links={[
            { to: '/generator', label: 'Онлайн-генератор' },
            { to: '/pricing', label: 'Тарифи' },
            { to: '/docs', label: 'Документація API' },
            { to: '/docs#decode', label: 'Розбір QR-коду' },
          ]}
        />
        <FooterColumn
          title="Ресурси"
          links={[
            { to: '/standard', label: 'Про стандарт НБУ' },
            { to: '/docs#errors', label: 'Коди помилок' },
            { href: '/api/docs', label: 'Swagger UI', external: true },
            { href: '/api/openapi.json', label: 'OpenAPI-схема', external: true },
          ]}
        />
        <FooterColumn
          title="Компанія"
          links={[
            { to: '/terms', label: 'Умови використання' },
            { to: '/privacy', label: 'Політика конфіденційності' },
            { href: 'mailto:api@qr24.app', label: 'api@qr24.app', external: true },
          ]}
        />
      </div>

      <div className="border-t border-slate-200 py-5 dark:border-slate-800">
        <div className="container-page flex flex-col items-center justify-between gap-2 text-xs text-slate-500 sm:flex-row dark:text-slate-400">
          <p>© {new Date().getFullYear()} QR24. Усі права захищено.</p>
          <p>Зроблено в Україні 🇺🇦</p>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({
  title,
  links,
}: {
  title: string
  links: { to?: string; href?: string; label: string; external?: boolean }[]
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="mt-3 space-y-2 text-sm text-slate-500 dark:text-slate-400">
        {links.map((link) => (
          <li key={link.label}>
            {link.external ? (
              <a href={link.href} className="hover:text-brand-600 dark:hover:text-brand-400">
                {link.label}
              </a>
            ) : (
              <Link to={link.to!} className="hover:text-brand-600 dark:hover:text-brand-400">
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
